import { Nullable, Identity, Community, Image } from 'core/types';
import { useTranslation } from 'hooks';
import { useCallback } from 'react';
import { getBaseUrl } from 'utils/url';
import { sleep } from 'utils/delay';
import { ButtonSection } from 'components/common/form/button-section';
import {
	Stack,
	Box,
	TextField,
	Form,
	Field,
	Typography,
	PickerFile,
	Heading,
	ImagePickerField,
	Link,
	FieldForSubscription,
	FormOnSubmitReturn,
	FormStack,
} from '../../../common';

export type SetupCommunityProfileSubmittedValues = {
	community: {
		slug: string;
		image: Nullable<PickerFile>;
	};
	identity: {
		image: Nullable<PickerFile>;
	};
};

type FormValues = {
	communitySlug: string;
	communityImage: Nullable<PickerFile>[] | undefined;
	identityImage: Nullable<PickerFile>[] | undefined;
};

type SetupCommunityProfileFormProps = {
	identity: Identity;
	community: Community;
	initialValues: FormValues;
	onSubmit?: (
		values: SetupCommunityProfileSubmittedValues
	) => FormOnSubmitReturn;
};

export const SetupCommunityProfileForm = (
	props: SetupCommunityProfileFormProps
) => {
	const {
		onSubmit,
		initialValues,
		community: currentCommunity,
		identity: currentIdentity,
	} = props;

	const { t } = useTranslation();

	const handleOnSubmit = useCallback(
		async (values: FormValues) => {
			const { communitySlug, communityImage, identityImage } = values;

			await sleep(1);

			const result = onSubmit?.({
				identity: {
					image: identityImage?.[0] ? identityImage[0] : undefined,
				},
				community: {
					slug: communitySlug.trim(),
					image: communityImage?.[0] ? communityImage[0] : undefined,
				},
			});

			return Promise.resolve(result).catch(error => error);
		},
		[onSubmit, currentCommunity, currentIdentity]
	);

	return (
		<Form
			onSubmit={handleOnSubmit}
			initialValues={initialValues}
			subscription={{ submitting: true }}
			keepDirtyOnReinitialize
		>
			{({ handleSubmit, submitting }) => (
				<form
					id='setup-community-profile-form'
					onSubmit={handleSubmit}
					noValidate
				>
					<FormStack>
						<Box component='section'>
							<Heading variant='h5'>
								{t('community-form.community-details')}
							</Heading>
							<Stack spacing={6}>
								<Box component='dl'>
									<Typography component='dt'>
										{t('community-form.community-name')}:
									</Typography>
									<Typography component='dd'>
										{currentCommunity.name}
									</Typography>
								</Box>
								<Box component='dl'>
									<Typography component='dt'>
										{t('community-form.community-type')}:
									</Typography>
									<Typography component='dd'>
										{currentCommunity.purpose.name}
									</Typography>
								</Box>
								<Box component='dl'>
									<Typography component='dt'>
										{t('community-form.community-location')}
										:
									</Typography>
									<Typography component='dd'>
										{currentCommunity.city}
										{currentCommunity.state
											? `, ${currentCommunity.state}`
											: ''}
									</Typography>
								</Box>
							</Stack>
						</Box>
						<Box component='section'>
							<Heading variant='h5'>
								{t('community-form.add-a-community-photo')}
							</Heading>
							<Typography mb={1}>
								{t('community-form.upload-a-photo-that')}.{' '}
								{t(
									'community-form.you-can-change-this-at-any-time'
								)}
								.
							</Typography>
							<Box maxWidth={300}>
								<Field
									name='communityImage'
									component={ImagePickerField}
								/>
							</Box>
						</Box>
						<Box component='section'>
							<Heading variant='h5'>
								{t('community-form.owner-details')}
							</Heading>
							<Stack spacing={6}>
								<Box component='dl'>
									<Typography component='dt'>
										{t('community-form.name')}:
									</Typography>
									<Typography component='dd'>
										{currentIdentity.first_name}{' '}
										{currentIdentity.last_name}
									</Typography>
								</Box>
								<Box component='dl'>
									<Typography component='dt'>
										{t('community-form.email')}:
									</Typography>
									<Typography component='dd'>
										<Link
											href={`mailto:${currentIdentity.email}`}
										>
											{currentIdentity.email}
										</Link>
									</Typography>
								</Box>
							</Stack>
						</Box>
						<Box component='section'>
							<Heading variant='h5' mb={1}>
								{t('community-form.profile-photo')}
							</Heading>
							<Box maxWidth={300}>
								<Field
									name='identityImage'
									component={ImagePickerField}
								/>
							</Box>
						</Box>
						<Box component='section'>
							<Heading variant='h5'>
								{t('community-form.set-your-community-url')}
							</Heading>
							<Typography mb={1}>
								{t('community-form.share-the-url')}.
							</Typography>
							<Box maxWidth={400}>
								<Field
									name='communitySlug'
									component={TextField}
									placeholder={currentCommunity.slug}
									fullWidth
								/>
							</Box>
							<FieldForSubscription
								name='communitySlug'
								subscription={{ value: true }}
							>
								{({ input }) => (
									<Typography mb={1} mt={2} color='primary'>
										{`${getBaseUrl()}/community/${
											input.value
										}`}
									</Typography>
								)}
							</FieldForSubscription>
						</Box>
						<ButtonSection
							submitting={submitting}
							submitText={t('community-form.update')}
							submittingText={t('community-form.updating')}
						/>
					</FormStack>
				</form>
			)}
		</Form>
	);
};
